import React, { useEffect, useState } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useParams } from 'react-router-dom';
import * as R from 'ramda';

// Utils
import {
  cardsListStyles,
  cardsPanelContainerStyles,
  SELF_AUTH_TABS,
} from 'feature/loading-management/utils/const';
import { useSetLoadingTitle, useUserRolePermissions } from 'hooks';

// Components
import { Heading, ListTabs } from 'feature/loading-management/shared-components';
import CardSkeletonList from '../../card-skeleton/CardSkeletonList';
import CardsList from './CardsList';
import HeaderButtons from './header-buttons/HeaderButtons.connector';

const CardsPanel = ({
  activeSelfAuthTab,
  currentSelection,
  selfAuthorizationCards,
  selfAuthorizations,
  selfAuthorizationsPending,
  setActiveSelfAuthTab,
}) => {
  const { viewBy } = useParams();
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });

  // permissions related to this selector are defined in CASREF-1420
  // https://issues.mmplp.net:8443/browse/CASREF-1420
  const { isSupplier, isInventoryOwner } = useUserRolePermissions();

  const [tabs, setTabs] = useState(SELF_AUTH_TABS);
  const [labelCounts, setLabelCounts] = useState({});

  const title = useSetLoadingTitle(selfAuthorizationsPending, currentSelection);

  // Settings tabs is in useState to use with useEffect
  // Prevents react console errors for attempting to set a tab to unavailable value
  useEffect(() => {
    const filtered = str => R.filter(R.propEq('id', str), SELF_AUTH_TABS);
    // function inside useEffect since it is only used here
    const getTabs = () => {
      let availableTabs = SELF_AUTH_TABS;
      if (viewBy === 'location') {
        if (isInventoryOwner && !isSupplier) {
          availableTabs = SELF_AUTH_TABS.filter(({ id }) => id !== 'supplierDirectedBlends');
        }
        if (!isInventoryOwner && isSupplier) {
          availableTabs = SELF_AUTH_TABS.filter(({ id }) => id === 'supplierDirectedBlends');
          setActiveSelfAuthTab('supplierDirectedBlends');
        }
        return availableTabs;
      }
      // To check for proprietary product as null for filtering and updating tabs
      if (R.isNil(currentSelection.proprietaryProduct)) {
        return filtered('baseProducts');
      }
      if (viewBy === 'product') {
        availableTabs = [];
        if (isInventoryOwner) {
          availableTabs = R.concat(availableTabs, filtered('customProducts'));
        }
        if (isSupplier) {
          availableTabs = R.concat(availableTabs, filtered('supplierDirectedBlends'));
        }
        return availableTabs;
      }
      return availableTabs;
    };
    // avoid doing anything if there is no selection, i.e. when typing in a search term
    // without this, if you are viewing a product with CP and SDB and type in a search term
    // the cards will dissappear and tabs will default to just "Base"
    if (!currentSelection.nameShort) return;
    const currentTabs = getTabs();
    setTabs(currentTabs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelection]);

  useEffect(() => {
    if (!R.isEmpty(selfAuthorizations)) {
      const counts = {};
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < SELF_AUTH_TABS.length; i++) {
        const counters = selfAuthorizations[SELF_AUTH_TABS[i].id];
        counts[SELF_AUTH_TABS[i].shortName] = `${R.length(
          // as some active authorizations can have active:false
          // we need to account for this and only return true
          // Issue related to CASREF-2726
          counters.filter(({ activeAuthorization }) => activeAuthorization?.active)
        )}${lgUp ? ' of ' : '/'}${R.length(counters)}`;
      }
      setLabelCounts(counts);
    }
  }, [selfAuthorizations, lgUp]);

  const renderCounts = () => {
    const tab = tabs?.find(({ id }) => id === activeSelfAuthTab);
    return `${tab?.name} (${labelCounts[tab?.shortName]})`;
  };

  return (
    <Box {...cardsPanelContainerStyles}>
      <Heading
        headerButtons={<HeaderButtons />}
        listTabs={
          <ListTabs
            activeTab={activeSelfAuthTab}
            labelCounts={labelCounts}
            onChange={setActiveSelfAuthTab}
            pendingState={selfAuthorizationsPending}
            singleChip
            tabs={tabs}
          />
        }
        showTabs
        title={title}
      />
      {!lgUp && !selfAuthorizationsPending && (
        // H6 styling due to when Supplier Directed Blends the title wraps
        // and the counts are shown on a new line...
        <Typography sx={{ fontWeight: '400', pb: 1, pl: 1 }} variant="h6">
          {renderCounts()}
        </Typography>
      )}
      <Box {...cardsListStyles} pb={7}>
        {selfAuthorizationsPending ? (
          <CardSkeletonList />
        ) : (
          <CardsList
            cards={selfAuthorizationCards}
            currentSelectionNameShort={currentSelection.nameShort}
            listType={activeSelfAuthTab}
          />
        )}
      </Box>
    </Box>
  );
};

CardsPanel.propTypes = {
  activeSelfAuthTab: string,
  currentSelection: shape({
    nameShort: string,
  }),
  selfAuthorizationCards: arrayOf(shape({})).isRequired,
  selfAuthorizations: shape({}).isRequired,
  selfAuthorizationsPending: bool.isRequired,
  setActiveSelfAuthTab: func.isRequired,
};

CardsPanel.defaultProps = {
  activeSelfAuthTab: '',
  currentSelection: {
    nameShort: '',
  },
};

export default CardsPanel;
